import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import DescriptorT from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';

import LeftArrow from '@/assets/icon-arrow-left.svg?react';
import RightArrow from '@/assets/icon-arrow-right.svg?react';
import {
    descriptorDimensions,
    RapidStorSettings,
    showPricesButtonText,
} from '@/core/utils/rapidstor';

import Button from '@/core/components/elements/button';

import {
    descriptorTitle,
    isDescriptorAvailable,
    sizeLabel,
    sortUnitTypes,
} from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

import DescriptorLabel from './label';
import DescriptorTag from './tag';

interface Props extends React.HTMLAttributes<HTMLElement> {
    descriptor: DescriptorT;
    carousel: boolean;
    active?: boolean;
    selectedIndex?: number;
    total?: number;
    setSelectedIndex?: (index: number) => void;
    onSelect: () => void;
}

function Descriptor({
    descriptor, onSelect, carousel, active, selectedIndex, setSelectedIndex, total,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const sortedUnitTypes = sortUnitTypes(descriptor.unitTypes || [], location);

    const descriptorTitleClasses: Record<string | number, string> = {
        0: classes.descriptorTitleLabel,
        1: classes.descriptorAreaLabel,
        2: classes.descriptorDimensionsLabel,
    };

    // The button copy is dynamic: a RapidStor setting when units are unavailable
    // or prices are hidden, otherwise a translation. The setting reads happen in
    // this memo (a function boundary), so the rs-setting build plugin can't see
    // them — we declare the source path here and pass it as data-rs-setting-dynamic
    // so the runtime marker flags the button only on the setting branches.
    const { buttonText, buttonSetting } = useMemo<{
        buttonText: string;
        buttonSetting?: string;
    }>(() => {
        if (!isDescriptorAvailable(descriptor)) {
            return {
                buttonText: location.rapidstor.noAvailableUnitsText,
                buttonSetting: 'location.rapidstor.noAvailableUnitsText',
            };
        }
        if (location.rapidstor.hidePrices) {
            return {
                buttonText: showPricesButtonText(location, true),
                buttonSetting: 'location.rapidstor.showPricesButtonText',
            };
        }
        return { buttonText: t('cta:continue') };
    }, [descriptor, location, t]);

    return (
        <div
            className={`${carousel ? classes.carousel_descriptor : classes.descriptor}`}
            data-testid={`descriptor-${descriptor.name}`}
            data-active={active ? 'true' : 'false'}
        >
            <div className={`${classes.header}`}>
                <div className={classes.left}>
                    <DescriptorLabel
                        descriptor={descriptor}
                        unitType={sortedUnitTypes[0]}
                    />
                </div>

                <div className={classes.right}>
                    <Button
                        onclick={onSelect}
                        type="primary"
                        size="small"
                        testId="descriptor-button"
                        id="continue_button"
                        data-rs-setting-dynamic={buttonSetting}
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>
            <div className={classes.icon}>
                <img
                    alt="Descriptor"
                    src={descriptor.picture || location.skin.defaultUnitPicture}
                    style={{
                        opacity: isDescriptorAvailable(descriptor) || RapidStorSettings(location).alwaysAllowPricingPage() ? 1 : 0.5,
                    }}
                />
            </div>
            {!carousel && window.rapidstor_settings.app_config.journey.showArrowsOnDescriptorMobile ? (
                <div className={classes.mobile_buttons}>
                    <button
                        type="button"
                        onClick={() => {
                            if (setSelectedIndex) {
                                setSelectedIndex((selectedIndex || 0) - 1);
                            }
                        }}
                        disabled={selectedIndex === 0}
                    >
                        <LeftArrow />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (setSelectedIndex) {
                                setSelectedIndex((selectedIndex || 0) + 1);
                            }
                        }}
                        disabled={window.rapidstor_settings.rapidstor_options.enabledCarouselEndCTA ? selectedIndex === total : selectedIndex === (total || 0) - 1}
                    >
                        <RightArrow />
                    </button>
                </div>
            ) : null}
            <div
                className={`${classes.active}`}
                style={{
                    display: carousel && active ? 'flex' : 'none',
                }}
            >
                <div className={classes.stats}>
                    <span
                        className={`${descriptorTitleClasses[
                            descriptorTitle(descriptor, location)[0]
                        ]
                        }`}
                    >
                        {descriptorTitle(descriptor, location)[1]}
                    </span>
                    <span hidden={descriptorTitle(descriptor, location)[0] === 0}>
                        {sizeLabel(descriptor.area, location)}
                    </span>
                </div>
                <span hidden={descriptorTitle(descriptor, location)[0] === 2}>
                    {descriptorDimensions(sortedUnitTypes[0], location)}
                </span>
            </div>
            <div className={carousel ? classes.hidden : classes.desktop_info}>
                <div className={`${classes.desktop_header}`}>
                    <div className={classes.left}>
                        <div className={classes.stats}>
                            <span
                                className={`${descriptorTitleClasses[
                                    descriptorTitle(descriptor, location)[0]
                                ]
                                }`}
                            >
                                {descriptorTitle(descriptor, location)[1]}
                            </span>
                            <span hidden={descriptorTitle(descriptor, location)[0] === 0}>
                                {sizeLabel(descriptor.area, location)}
                            </span>
                        </div>
                        <span hidden={descriptorTitle(descriptor, location)[0] === 2}>
                            {descriptorDimensions(sortedUnitTypes[0], location)}
                        </span>
                    </div>

                    <div className={classes.right}>
                        <Button
                            onclick={onSelect}
                            type="primary"
                            size="small"
                            extClasses={`${classes.desktop_button}`}
                            testId="descriptor-button"
                            id="continue_button"
                            data-rs-setting-dynamic={buttonSetting}
                        >
                            {buttonText}
                            <RightArrow className={classes.arrow} />
                        </Button>
                    </div>
                </div>
                <div className={classes.desktop_body}>
                    <span>{descriptor.specialText}</span>
                    <p>{descriptor.description}</p>
                    {descriptor.tags && descriptor.tags.length > 0 && (
                        <>
                            <span className={`${classes.ideal_for}`}>
                                {t('descriptor:idealFor')}
                            </span>
                            <DescriptorTag tags={descriptor.tags} />
                        </>
                    )}
                    {window.rapidstor_settings.rapidstor_options.dev && (
                    // used for testing purposes
                        <div style={{ marginTop: 20 }}>
                            <pre>
                                {descriptor.unitTypes?.reduce(
                                    (acc, unitType) => acc + unitType.totalAvailable,
                                    0,
                                    // eslint-disable-next-line i18next/no-literal-string
                                )}
                                {' Available Units'}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.footer}>
                <span>{descriptor.specialText}</span>
                <span>{descriptor.description}</span>
            </div>
        </div>
    );
}

export default Descriptor;
